import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '@kmgestao/i18n';
import { Input, PageContainer, PageIntroCard, PageSectionCard, Skeleton } from '@kmgestao/ui';

import { CLINIC_STATUSES, clinicDisplayName, useClinics } from '../../../features/clinics/index.js';
import { SuperadminShell } from '../SuperadminShell.js';

import { ClinicStatusBadge } from './ClinicStatusBadge.js';

/**
 * Tela superadmin com lista de clínicas provisionadas.
 *
 * Pareada com `LeadsListPage`: lead que foi `signed` → `provisioned`
 * vira clínica e aparece aqui. Cada clinic-row faz link pro detalhe
 * (`/superadmin/clinics/:id`) onde Karla vê subscription + members
 * + timeline de provisionamento.
 *
 * **Padrão responsivo** mantém o mesmo do `LeadsListPage`:
 *   - `< sm`: lista de cards empilhados
 *   - `≥ sm`: tabela densa com colunas
 *
 * Sem ações destrutivas aqui — superadmin não pausa/cancela clínica
 * pela lista (evita acidente). Essas ações ficam no detalhe com
 * confirm.
 *
 * Strings em pt-BR vêm do namespace i18n `clinics` (regra inviolável
 * §5.3 do AGENTS.md). Fallback de status desconhecido cai pro próprio
 * key raw (não quebra UI).
 */
export function ClinicsListPage() {
  const { t } = useT('clinics');
  const { data: clinics, isLoading, isError, error } = useClinics();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!clinics) return [];
    if (!search.trim()) return clinics;
    const q = search.trim().toLowerCase();
    return clinics.filter((c) => {
      const name = clinicDisplayName(c).toLowerCase();
      const cnpj = (c.cnpj ?? '').toLowerCase();
      const email = (c.email ?? '').toLowerCase();
      return name.includes(q) || cnpj.includes(q) || email.includes(q);
    });
  }, [clinics, search]);

  return (
    <SuperadminShell>
      <PageContainer>
        <PageIntroCard
          eyebrow={
            <Link to="/superadmin/dashboard" className="hover:underline">
              {t('list.eyebrow')}
            </Link>
          }
          title={t('list.title')}
          description={t('list.description')}
        />

        <PageSectionCard
          title={isLoading ? t('list.loading') : t('list.count', { count: filtered.length })}
          description={t('list.searchHint')}
        >
          <div className="space-y-4">
            <Input
              type="search"
              placeholder={t('list.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-md"
              aria-label={t('list.searchPlaceholder')}
            />

            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : isError ? (
              <p className="text-sm text-destructive">
                {t('list.loadError', { message: (error as Error).message })}
              </p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {search.trim() ? t('list.emptySearch') : t('list.emptyAll')}
              </p>
            ) : (
              <>
                {/* Mobile: lista de cards */}
                <ul className="space-y-2 sm:hidden">
                  {filtered.map((clinic) => (
                    <li key={clinic.id}>
                      <Link
                        to={`/superadmin/clinics/${clinic.id}`}
                        className="block rounded-lg border border-border bg-card p-4 transition-colors hover:bg-muted/50"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0 space-y-1">
                            <p className="truncate font-semibold text-foreground">
                              {clinicDisplayName(clinic)}
                            </p>
                            <p className="truncate text-xs text-muted-foreground">
                              {clinic.cnpj || clinic.email || t('list.noContact')}
                            </p>
                          </div>
                          <ClinicStatusBadge status={clinic.status} />
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>

                {/* Desktop: tabela densa */}
                <div className="hidden overflow-x-auto sm:block">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="pb-2 pr-4 font-medium">{t('list.table.clinic')}</th>
                        <th className="pb-2 pr-4 font-medium">{t('list.table.cnpj')}</th>
                        <th className="pb-2 pr-4 font-medium">{t('list.table.email')}</th>
                        <th className="pb-2 pr-4 font-medium">{t('list.table.status')}</th>
                        <th className="pb-2 font-medium">{t('list.table.createdAt')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filtered.map((clinic) => (
                        <tr
                          key={clinic.id}
                          className="cursor-pointer transition-colors hover:bg-muted/50"
                        >
                          <td className="py-3 pr-4">
                            <Link
                              to={`/superadmin/clinics/${clinic.id}`}
                              className="font-medium text-foreground hover:underline"
                            >
                              {clinicDisplayName(clinic)}
                            </Link>
                            {clinic.trade_name && clinic.legal_name !== clinic.trade_name ? (
                              <p className="text-xs text-muted-foreground">{clinic.legal_name}</p>
                            ) : null}
                          </td>
                          <td className="py-3 pr-4 font-mono text-xs text-muted-foreground">
                            {clinic.cnpj || '—'}
                          </td>
                          <td className="py-3 pr-4 text-muted-foreground">{clinic.email || '—'}</td>
                          <td className="py-3 pr-4">
                            <ClinicStatusBadge status={clinic.status} />
                          </td>
                          <td className="py-3 text-muted-foreground">
                            {formatDateBR(clinic.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </PageSectionCard>

        {/* Helper rodapé pra Karla saber que existem outros estados além
            dos que aparecem na lista — útil quando ela não vê uma clínica
            específica e quer confirmar que não está só filtrada. */}
        <p className="text-center text-xs text-muted-foreground">
          {t('list.knownStatusesPrefix')}{' '}
          {CLINIC_STATUSES.map((key) => `${t(`status.${key}`)} (${key})`).join(' · ')}
        </p>
      </PageContainer>
    </SuperadminShell>
  );
}

function formatDateBR(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return '—';
  }
}
